import { useField, FieldInputProps } from 'formik';
import React from 'react';

import Input, { ComponentProps as InputProps } from './Input';
import * as Styled from './styled';

type LabelProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;

/**
 * Date component properties
 */
export type ComponentProps = Pick<FieldInputProps<string>, 'name'> &
  Pick<InputProps, 'ariaLabel' | 'mode' | 'divider'> & {
    /**
     * Label to show at top of the date
     */
    label?: string;
    /**
     * Optional divider char between each date section (defaults to '/')
     */
    divider?: string;
    /**
     * (Optional) Disabled the date input from been editable
     */
    disabled?: boolean;
    /**
     * Optional Label styled component props
     */
    errorLabelProps?: LabelProps;
  };

export const DateInput: React.FC<ComponentProps> = ({
  name,
  label = '',
  ariaLabel,
  divider = '/',
  mode,
  disabled = false,
  errorLabelProps
}) => {
  const [field, meta, helpers] = useField<string>(name);

  const { onBlur } = field;

  /**
   * Displaying a user error message
   */
  const showError = meta.touched && meta.error;

  // onChange is a debounced event
  field.onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      helpers.setValue(event.target.value.replace(/\s/g, ''), true);
    },
    [helpers]
  );

  // onBlur is not debounced - we will see this act on the latest value
  field.onBlur = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.persist();
      helpers.setValue(event.target.value.replace(/\s/g, ''), true);
      onBlur(event);
    },
    [helpers, onBlur]
  );

  return (
    <Styled.Container>
      <Styled.Label htmlFor={name} id={`${name}.label`}>
        {label}
      </Styled.Label>
      <Input
        field={field}
        meta={meta}
        mode={mode}
        ariaLabel={ariaLabel}
        divider={divider}
        disabled={disabled}
      />
      {showError && <Styled.Small {...errorLabelProps}>{meta.error}</Styled.Small>}
    </Styled.Container>
  );
};

export default DateInput;
